'use client'

import { useState, useEffect, useRef, useCallback } from 'react'
import { LogEntry } from './LogEntry'
import { LogFilters } from './LogFilters'

// ============================================================================
// Types
// ============================================================================

export interface ActivityLogEntry {
  id: string
  timestamp: Date
  type: 'task_created' | 'task_started' | 'task_completed' | 'task_error' | 'task_review' | 'task_approved' | 'task_rejected'
  taskId: string
  taskTitle: string
  message: string
  metadata?: Record<string, unknown>
}

interface ActivityLogProps {
  className?: string
  maxEntries?: number
  autoScroll?: boolean
  showFilters?: boolean
}

type FilterType = 'all' | 'running' | 'completed' | 'error' | 'review'

// ============================================================================
// Component
// ============================================================================

export function ActivityLog({
  className = '',
  maxEntries = 100,
  autoScroll = true,
  showFilters = true
}: ActivityLogProps) {
  const [entries, setEntries] = useState<ActivityLogEntry[]>([])
  const [filter, setFilter] = useState<FilterType>('all')
  const [isPaused, setIsPaused] = useState(false)
  const [search, setSearch] = useState('')
  const logEndRef = useRef<HTMLDivElement>(null)

  // ============================================================================
  // Data Fetching
  // ============================================================================

  const fetchLogs = useCallback(async () => {
    try {
      const response = await fetch('https://api.lldonha.com/api/tasks?limit=50')
      const data = await response.json()

      if (data.success) {
        // Convert tasks to log entries
        const newEntries: ActivityLogEntry[] = data.tasks.map((task: any) => {
          let type: ActivityLogEntry['type'] = 'task_created'
          let message = `Task criada: ${task.titulo}`

          if (task.status === 'running') {
            type = 'task_started'
            message = `Task em execução: ${task.titulo}`
          } else if (task.status === 'done' || task.status === 'approved') {
            type = 'task_completed'
            message = `Task concluída: ${task.titulo}`
          } else if (task.status === 'error') {
            type = 'task_error'
            message = `Task com erro: ${task.titulo}`
          } else if (task.status === 'needs_review') {
            type = 'task_review'
            message = `Task aguardando review: ${task.titulo}`
          }

          return {
            id: task.id,
            timestamp: new Date(task.created_at || Date.now()),
            type,
            taskId: task.id,
            taskTitle: task.titulo,
            message,
            metadata: {
              status: task.status,
              prioridade: task.prioridade
            }
          }
        })

        setEntries(newEntries.slice(0, maxEntries))
      }
    } catch (error) {
      console.error('Error fetching logs:', error)
    }
  }, [maxEntries])

  useEffect(() => {
    fetchLogs()

    // Auto-refresh se não pausado
    if (!isPaused) {
      const interval = setInterval(fetchLogs, 3000)
      return () => clearInterval(interval)
    }
  }, [fetchLogs, isPaused])

  // Auto-scroll para fim
  useEffect(() => {
    if (autoScroll && !isPaused) {
      logEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [entries, autoScroll, isPaused])

  // ============================================================================
  // Filtering
  // ============================================================================

  const filteredEntries = entries.filter(entry => {
    // Filter by type
    if (filter !== 'all') {
      const typeMap: Record<FilterType, ActivityLogEntry['type'][]> = {
        all: [],
        running: ['task_started'],
        completed: ['task_completed', 'task_approved'],
        error: ['task_error', 'task_rejected'],
        review: ['task_review']
      }

      if (!typeMap[filter].includes(entry.type)) return false
    }

    // Filter by search
    if (search) {
      const searchLower = search.toLowerCase()
      return (
        entry.taskTitle.toLowerCase().includes(searchLower) ||
        entry.message.toLowerCase().includes(searchLower) ||
        entry.taskId.toLowerCase().includes(searchLower)
      )
    }

    return true
  })

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleClear = () => {
    setEntries([])
  }

  const handleExport = () => {
    const exportData = JSON.stringify(entries, null, 2)
    const blob = new Blob([exportData], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `activity-log-${new Date().toISOString()}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className={`flex flex-col h-full bg-gray-50 dark:bg-gray-900 ${className}`}>
      {/* Header */}
      <div className="px-6 py-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              📊 Activity Log
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Monitoramento em tempo real
            </p>
          </div>

          <div className="flex items-center gap-2">
            {/* Pause/Resume */}
            <button
              onClick={() => setIsPaused(!isPaused)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                isPaused
                  ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
              title={isPaused ? 'Retomar' : 'Pausar'}
            >
              {isPaused ? '▶️ Retomar' : '⏸️ Pausar'}
            </button>

            {/* Export */}
            <button
              onClick={handleExport}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Exportar logs"
            >
              💾 Exportar
            </button>

            {/* Clear */}
            <button
              onClick={handleClear}
              className="px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              title="Limpar logs"
            >
              🗑️ Limpar
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por título, ID ou mensagem..."
            className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
          />

          <span className="text-sm text-gray-500 dark:text-gray-400">
            {filteredEntries.length} / {entries.length}
          </span>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="mt-3">
            <LogFilters
              currentFilter={filter}
              onFilterChange={setFilter}
              counts={{
                all: entries.length,
                running: entries.filter(e => e.type === 'task_started').length,
                completed: entries.filter(e => ['task_completed', 'task_approved'].includes(e.type)).length,
                error: entries.filter(e => ['task_error', 'task_rejected'].includes(e.type)).length,
                review: entries.filter(e => e.type === 'task_review').length
              }}
            />
          </div>
        )}
      </div>

      {/* Log Entries */}
      <div className="flex-1 overflow-y-auto bg-gray-900 font-mono text-sm">
        {filteredEntries.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            {entries.length === 0 ? (
              <div className="text-center">
                <p className="mb-2">📋 Nenhum log ainda</p>
                <p className="text-xs">Logs aparecerão aqui em tempo real</p>
              </div>
            ) : (
              <div className="text-center">
                <p className="mb-2">🔍 Nenhum resultado</p>
                <p className="text-xs">Tente ajustar os filtros</p>
              </div>
            )}
          </div>
        ) : (
          <div className="p-4 space-y-1">
            {filteredEntries.map((entry) => (
              <LogEntry key={entry.id} entry={entry} />
            ))}
            <div ref={logEndRef} />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-2 bg-gray-800 border-t border-gray-700 flex items-center justify-between text-xs text-gray-400">
        <div className="flex items-center gap-4">
          <span>
            {isPaused ? '⏸️ Pausado' : '🔄 Atualizando...'}
          </span>
          <span>•</span>
          <span>
            Max: {maxEntries} entradas
          </span>
        </div>

        <div>
          Última atualização: {new Date().toLocaleTimeString('pt-BR')}
        </div>
      </div>
    </div>
  )
}
